////////////////////////////////////////////////////////////////////////////////
//
// Store auth module
//
////////////////////////////////////////////////////////////////////////////////

import { TokenService } from "@/services/token.service";
import AccountService from "@/services/account.service";
import ApiService from "@/services/api.service";
import { Mutation } from "@/store/mutations";
import { Action } from "@/store/actions";

////////////////////////////////////////////////////////////////////////////////

const state = {
  authError: null,
  account: null,
  rights: AccountService.defaultRights,
  isAuthenticated: !!TokenService.token(),
  claims: TokenService.getClaims(), // Store claims in state
  hasAllAccess: null,
  hasAccessto: null,
  accountSubtype: null,
   refreshTimeout: null,
     refreshAttempts: 0,
  lastRefreshError: null,
    refreshHistory: [],
  optimalRefreshRatio: 0.7 
};

////////////////////////////////////////////////////////////////////////////////

const getters = {
  username: (state) => (state.account ? state.account.name : ""),
  entity: (state) => (state.account ? state.account._entity : ""),
  usernameShort: (state) => {
    if (state.account) {
      return state.account.name
        ? state.account.name.substr(0, 2).toLocaleUpperCase()
        : "";
    } else {
      return "";
    }
  },
  isAuthenticated: (state) => state.isAuthenticated,
  hasAllAccess: (state) => {
    if (state.claims && state.claims.permissions) {
      return state.claims.permissions.includes("all");
    }
    return null;
  },
  
  hasAccessto: (state) => {
    if (state.claims && state.claims.permissions) {
      return state.claims.permissions;
    }
     return []; 
  },
  
  accountSubtype: (state) => {
    if (state.claims && state.claims.subtype) {
      return state.claims.subtype;
    }
    return null;
  },
};

////////////////////////////////////////////////////////////////////////////////

const mutations = {
  [Mutation.auth.error](state, error) {
    state.authError = error;
  },

  [Mutation.auth.set](state, data) {
    state.error = null;
    state.isAuthenticated = true;
    TokenService.tokenSave(data.access_token);
    TokenService.refreshSave(data.refresh_token);
    TokenService.usernameSave(data.logged_in_as);
    TokenService.entitySave(data.entity);
  },

  [Mutation.auth.setAccount](state, data) {
    state.account = data;
    state.rights = AccountService.getRights(data.subtype);
  },

  [Mutation.auth.delete](state) {
    state.isAuthenticated = false;
    state.error = null;
    TokenService.tokenDelete();
    TokenService.refreshDelete();
    TokenService.usernameDelete();
    TokenService.entityDelete();
  },
  [Mutation.auth.setRefreshTimeout](state, timeoutId) {
    state.refreshTimeout = timeoutId;
  },
  
  [Mutation.auth.clearRefreshTimeout](state) {
    if (state.refreshTimeout) {
      clearTimeout(state.refreshTimeout);
      state.refreshTimeout = null;
    }
  },
};

////////////////////////////////////////////////////////////////////////////////

const actions = {
  async [Action.auth.login](context, credentials) {
    context.commit(Mutation.auth.error, undefined);
    try {
      const login = await ApiService.auth.login(credentials);
      context.commit(Mutation.auth.set, login.data.data);
      context.commit(Mutation.entity, login.data.data.entity);
      ApiService.setHeader();
      const account = await ApiService.auth.getAccount(
        TokenService.username(),
        TokenService.entity()
      );
      context.commit(Mutation.auth.setAccount, account.data.data);
      context.dispatch('scheduleTokenRefresh');

    } catch (error) {
      context.commit(Mutation.auth.delete);
      if ([401, 403].includes(error.errorInfo.errorCode)) {
        context.commit(Mutation.auth.error, "Wrong password");
      } else {
        context.commit(Mutation.auth.error, error.errorInfo.userMessage);
      }
      return Promise.reject(error);
    }
  },

  [Action.auth.logout](context) {
    context.commit(Mutation.auth.delete);
  },

  async [Action.auth.check](context) {
    if (!TokenService.token() || !TokenService.entity()) {
      context.commit(Mutation.auth.delete);
      return;
    }
    context.commit(Mutation.entity, TokenService.entity());
    ApiService.setHeader();
    if (context.state.account) {
    context.dispatch('scheduleTokenRefresh');
    return;
  }
    try {
      const account = await ApiService.auth.getAccount(
        TokenService.username(),
        TokenService.entity()
      );
      context.commit(Mutation.auth.setAccount, account.data.data);
      if (!context.state.entity && TokenService.entity()) {
      context.commit(Mutation.entity, TokenService.entity());
    }
    
            context.dispatch('scheduleTokenRefresh');

    } catch (error) {
      context.commit(Mutation.auth.error, "Error loading user data");
      context.commit(Mutation.auth.delete);
    }
  },
 async refreshToken(context) {
 //console.log('DEBUG: refreshToken action called');
  try {
    const refreshToken = TokenService.refresh();
    const response = await ApiService.auth.refreshToken(refreshToken);
    const newToken = response.data.data.access_token;
    const newRefreshToken = response.data.data.refresh_token;

    // Reset attempt counter on success
    context.state.refreshAttempts = 0;
    
    // Update tokens
    TokenService.tokenSave(newToken);
    TokenService.refreshSave(newRefreshToken);
    ApiService.setHeader();
    
   //console.log('DEBUG: Tokens updated successfully');
    context.state.claims = TokenService.getClaims();
    
    // Reschedule the next refresh
    context.dispatch('scheduleTokenRefresh');
    
    return newToken;
  } catch (error) {
    // Increment attempt counter on failure
    context.state.refreshAttempts++;
    context.state.lastRefreshError = error;
    
    console.error("DEBUG: Token refresh failed, attempt:", context.state.refreshAttempts);
    console.error("DEBUG: Error details:", error.response?.data || error.message);
    
    // Don't logout immediately on first failure
    if (context.state.refreshAttempts >= 3) {
     //console.log('DEBUG: Max refresh attempts reached, logging out');
      context.commit(Mutation.auth.delete);
    } else {
      // Try again with backoff
      context.dispatch('scheduleTokenRefresh');
    }
    
    throw error;
  }
},
scheduleTokenRefresh(context) {
  context.commit(Mutation.auth.clearRefreshTimeout);
  
  if (!TokenService.token() || !TokenService.refresh()) {
   //console.log('DEBUG: No token or refresh token available');
    return;
  }

  let tokenExpiry = TokenService.getTokenExpiry();
  if (!tokenExpiry) return;

  if (tokenExpiry < 10000000000) tokenExpiry *= 1000;

  const now = Date.now();
  const tokenLifespan = tokenExpiry - now;
  
  if (tokenLifespan <= 0) {
    context.commit(Mutation.auth.delete);
    return;
  }
  
  // Learn from history if we have enough data
  if (context.state.refreshHistory.length >= 5) {
    const successfulRefreshes = context.state.refreshHistory.filter(
      entry => entry.success && entry.remainingTime > 0
    );
    
    if (successfulRefreshes.length > 0) {
      const avgRemainingRatio = successfulRefreshes.reduce(
        (sum, entry) => sum + (entry.remainingTime / entry.originalLifespan), 0
      ) / successfulRefreshes.length;
      
      // Adjust our optimal refresh ratio based on historical performance
      context.state.optimalRefreshRatio = Math.max(0.1, Math.min(0.9, avgRemainingRatio * 0.9));
     //console.log('DEBUG: Adjusted optimal refresh ratio to', context.state.optimalRefreshRatio);
    }
  }
  
  // Use the learned optimal ratio
  let refreshTime = tokenLifespan * context.state.optimalRefreshRatio;
  
  // Ensure minimum 5-second buffer
  refreshTime = Math.max(refreshTime - 5000, 5000);
 //console.log('DEBUG: Self-adjusted refresh time (ms):', refreshTime);

  const timeoutId = setTimeout(() => {
    const refreshStartTime = Date.now();
    context.dispatch('refreshToken').then(newToken => {
      const refreshDuration = Date.now() - refreshStartTime;
      const newTokenExpiry = TokenService.getTokenExpiry();
      const newLifespan = newTokenExpiry - Date.now();
      
      // Record this successful refresh for future learning
      context.state.refreshHistory.push({
        success: true,
        originalLifespan: tokenLifespan,
        remainingTime: newLifespan,
        refreshDuration: refreshDuration,
        timestamp: Date.now()
      });
      
      // Keep only the last 10 history entries
      if (context.state.refreshHistory.length > 10) {
        context.state.refreshHistory.shift();
      }
      
      // Immediately reschedule with the new token
      context.dispatch('scheduleTokenRefresh');
    }).catch(error => {
      // Record failed refresh
      context.state.refreshHistory.push({
        success: false,
        originalLifespan: tokenLifespan,
        timestamp: Date.now()
      });
      
      // Retry with a shorter interval
      const retryTime = Math.min(30000, 5000 * (context.state.refreshAttempts || 1));
      setTimeout(() => {
        context.dispatch('scheduleTokenRefresh');
      }, retryTime);
    });
  }, refreshTime);
  
  context.commit(Mutation.auth.setRefreshTimeout, timeoutId);
}

};

////////////////////////////////////////////////////////////////////////////////

const auth = {
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};
export default auth;
